import React, { useCallback, useEffect, useState } from 'react';
import {
    deleteCompat,
    DeviceMaterialCompat,
    listCompat,
} from '../../api/governance';
import { listProcessArchs } from '../../api/parameter';
import { useScopedDictText } from '../../utils/dictOptions';
import { ApiError } from '../../api/request';
import { useCan } from '../../store';
import CompatBatchUploadModal from './CompatBatchUploadModal';
import Pagination from '../../components/Pagination';
import BatchActions, {
    HeaderCheckbox,
    RowCheckbox,
} from '../../components/BatchActions';
import { useBatchSelect } from '../../utils/useBatchSelect';
import { confirmDialog, alertDialog } from '../../utils/dialog';

const PAGE_SIZE = 20;

function CompatList() {
    const canDelete = useCan('device', 'delete');
    const canEdit = useCan('device', 'update'); // 适配关系并入 device 域 (2026-09-12)
    // input* = 输入框临时值; 提交后的 deviceKey/xcode 才驱动 API
    const [inputDeviceKey, setInputDeviceKey] = useState('');
    const [inputMaterialId, setInputMaterialId] = useState('');
    const [deviceKey, setDeviceKey] = useState('');
    const [xcode, setXcode] = useState('');
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [list, setList] = useState<DeviceMaterialCompat[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [uploadOpen, setUploadOpen] = useState(false);

    // 批量操作 (2026-09-10): 适配关系是复合键 (deviceKey/xcode[/processType]),
    // targetCode 用与后端删除接口同一套拼法, buildDeleteData 再拆回三个字段.
    const compatTarget = (c: DeviceMaterialCompat) =>
        c.processType
            ? `${c.deviceKey}/${c.xcode}/${c.processType}`
            : `${c.deviceKey}/${c.xcode}`;
    const sel = useBatchSelect(
        list.map(compatTarget),
        [page, deviceKey, xcode].join('|'),
    );
    // 加工类型列展示字典 label (process_type 按框架 scoped; 行上无 arch 上下文, 全框架加载后合并查)
    const [archCodes, setArchCodes] = useState<string[]>([]);
    useEffect(() => {
        listProcessArchs()
            .then((as) => setArchCodes(as.map((a) => a.code)))
            .catch(() => {});
    }, []);
    const processTypeText = useScopedDictText('process_type', archCodes);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await listCompat({
                deviceKey,
                xcode,
                page,
                pageSize: PAGE_SIZE,
            });
            setList(res.list || []);
            setTotal(res.totalCount);
        } catch (e) {
            const msg =
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
            setError(msg);
            setList([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [deviceKey, xcode, page]);

    useEffect(() => {
        load();
    }, [load]);

    const onSearch = (e: React.FormEvent) => {
        e.preventDefault();
        const dk = inputDeviceKey.trim();
        const mid = inputMaterialId.trim();
        if (dk === deviceKey && mid === xcode && page === 1) {
            load(); // 同条件再点查询 = 强制刷新
            return;
        }
        setDeviceKey(dk);
        setXcode(mid);
        setPage(1);
    };

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-base font-semibold">
                        设备 × 材料 适配
                    </div>
                    <div className="text-xs text-slate-500">
                        声明哪些 (设备, 材料, 加工类型) 三元组是兼容的;
                        不兼容的须给出原因 · 共 {total} 条
                    </div>
                </div>
                <form onSubmit={onSearch} className="flex gap-2 items-center">
                    <input
                        className="inp w-44"
                        placeholder="device_key"
                        value={inputDeviceKey}
                        onChange={(e) => setInputDeviceKey(e.target.value)}
                    />
                    <input
                        className="inp w-44"
                        placeholder="xcode"
                        value={inputMaterialId}
                        onChange={(e) => setInputMaterialId(e.target.value)}
                    />
                    <button type="submit" className="btn-ghost">
                        查询
                    </button>
                    {canEdit && (
                        <BatchActions
                            resource="device_material_compat"
                            entityLabel="适配关系"
                            deleteKeyField="deviceKey"
                            buildDeleteData={(target) => {
                                const [dk, xc, pt] = target.split('/');
                                return {
                                    deviceKey: dk,
                                    xcode: xc,
                                    processType: pt || '',
                                };
                            }}
                            selected={sel.selectedKeys}
                            onClear={sel.clear}
                            onDone={load}
                        />
                    )}
                    {canEdit && (
                        <button
                            type="button"
                            className="btn-primary"
                            onClick={() => setUploadOpen(true)}
                        >
                            + 批量上传
                        </button>
                    )}
                </form>
            </div>

            {error && (
                <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                    {error}
                </div>
            )}

            <div className="card overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-slate-50">
                        <tr>
                            {canEdit && (
                                <th className="th w-8" hidden={!canDelete}>
                                    <HeaderCheckbox
                                        hidden={!canDelete}
                                        sel={sel}
                                    />
                                </th>
                            )}
                            <th className="th">device_key</th>
                            <th className="th">xcode</th>
                            <th className="th">加工类型</th>
                            <th className="th">是否兼容</th>
                            <th className="th">不兼容原因</th>
                            {canEdit && <th className="th">操作</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td
                                    className="td text-center text-slate-400"
                                    colSpan={canEdit ? 7 : 5}
                                >
                                    加载中…
                                </td>
                            </tr>
                        )}
                        {!loading && list.length === 0 && (
                            <tr>
                                <td
                                    className="td text-center text-slate-400"
                                    colSpan={canEdit ? 7 : 5}
                                >
                                    暂无数据
                                </td>
                            </tr>
                        )}
                        {list.map((c) => (
                            <tr key={c.id} className="hover:bg-slate-50">
                                {canEdit && (
                                    <td className="td" hidden={!canDelete}>
                                        <RowCheckbox
                                            hidden={!canDelete}
                                            sel={sel}
                                            k={compatTarget(c)}
                                        />
                                    </td>
                                )}
                                <td className="td font-mono text-xs">
                                    {c.deviceKey}
                                </td>
                                <td className="td font-mono text-xs">
                                    {c.xcode}
                                </td>
                                <td className="td">
                                    {c.processType
                                        ? processTypeText(
                                              undefined,
                                              c.processType,
                                          )
                                        : '(全部)'}
                                </td>
                                <td className="td">
                                    {c.isCompatible ? (
                                        <span className="tag cap-on">兼容</span>
                                    ) : (
                                        <span className="tag bg-rose-50 text-rose-700">
                                            不兼容
                                        </span>
                                    )}
                                </td>
                                <td className="td text-xs text-slate-500">
                                    {c.reason || '—'}
                                </td>
                                {canEdit && (
                                    <td className="td text-xs">
                                        <button
                                            type="button"
                                            className="btn-danger-text"
                                            onClick={async () => {
                                                if (
                                                    !(await confirmDialog(
                                                        `确认删除适配 ${c.deviceKey} × ${c.xcode}${c.processType ? ' × ' + c.processType : ''}?`,
                                                    ))
                                                )
                                                    return;
                                                try {
                                                    await deleteCompat({
                                                        deviceKey: c.deviceKey,
                                                        xcode: c.xcode,
                                                        processType:
                                                            c.processType,
                                                    });
                                                    await load();
                                                } catch (e) {
                                                    const msg =
                                                        e instanceof ApiError
                                                            ? `${e.code}: ${e.message}`
                                                            : String(e);
                                                    alertDialog(
                                                        '删除失败: ' + msg,
                                                    );
                                                }
                                            }}
                                        >
                                            删除
                                        </button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between text-xs text-slate-500">
                <span>共 {total} 条</span>
                <Pagination
                    page={page}
                    totalPages={totalPages}
                    onChange={setPage}
                />
            </div>

            <CompatBatchUploadModal
                open={uploadOpen}
                onClose={() => setUploadOpen(false)}
                onSaved={() => load()}
            />
        </div>
    );
}

export default CompatList;
